/**
 * Advisor-firm registry — the hard-coding seam for the advisor-firm channel v1
 * (docs/25-advisor-firm-channel.md §1). RIPL is compiled in; the self-service offering
 * re-implements the resolver functions below against appcore tenant records
 * (`CFG#advisor-firm`, mirroring `CFG#branding`) without touching any consumer, so
 * every consumer MUST go through the resolvers, never read `ADVISOR_FIRMS` directly.
 *
 * **steward-app#743 is that re-implementation, arriving as a DUAL READ.** Each resolver now
 * takes a {@link FirmLookupContext} — an environment plus an optional
 * {@link AdvisorFirmDirectory}, the document a `steward-bg-firm-directory` Lambda publishes
 * from appcore's firm records (docs/32 §6 step 5). Precedence is **env first, directory
 * second, and NEVER a merge across the two**: a firm resolves entirely from one source or
 * entirely from the other, or not at all. A half-env/half-directory firm is exactly the
 * divergence steward-app#666 already paid for once (`deploy/lib/advisor-firm-contract.ts`
 * carries that record), and a firm whose ids came from one place and whose name came from
 * another is a firm nobody can reason about in an incident. Concretely, that precedence is one
 * rule and it lives in `servableRecords` below: **the directory never answers for a slug this
 * environment's env leg actually configures**. Read that function's note before changing any
 * resolver — in particular the 2026-09-03 stage incident (steward-app#1212) recorded there,
 * which is why the rule is keyed on "configured here" rather than on "compiled in at all".
 *
 * Three layers, split by where the values may live:
 *   - Static defs (`AdvisorFirmDef`): identity that is safe to compile into both the
 *     SPA and the façade — slug, display name, brand key, branded hostnames.
 *   - Environment config (`AdvisorFirm`): per-env ids and addresses (tenant id, firm
 *     workspace id, advisor emails) supplied via env vars on the façade/writer only
 *     (`deploy/lib/api-stack.ts`), NEVER compiled in. With the env vars unset **and no
 *     record published for the slug**, a firm is simply not configured: `resolveAdvisorFirm`
 *     returns null (the public intake route 404s, advisor routes return empty) — which is
 *     what makes deploy-before-seed ordering safe in a fresh environment.
 *
 *     ⚠️ **Since steward-app#1212, unsetting the variables is no longer a KILL SWITCH for a
 *     compiled firm.** If a record exists for the slug, the directory answers instead of
 *     nothing — which is the whole point of that fix, and the price is that "delete the env
 *     vars" no longer turns a firm off. The way to turn a firm off is `status: 'disabled'`
 *     on its record (`activeDirectoryRecords`), which is the same lever every
 *     directory-only firm has always had, on the operator surface that owns the record.
 *   - The DIRECTORY (`AdvisorFirmDirectory`, `advisorFirmDirectory.ts`): the same identity
 *     and the same per-env values, published as data rather than compiled and deployed.
 *     Every field on it is validated at parse — see that module's header for why each
 *     check is a security control rather than tidiness.
 *
 * Every resolved firm carries {@link AdvisorFirm.source}. It is a server constant (`'env'`
 * or `'directory'`), safe in a log line, and it is how you prove in dev that a cutover
 * actually happened rather than the env leg quietly still answering.
 *
 * Naming rule (docs/25): RIPL's "Financial Snapshot" is UI copy only; all code
 * identifiers here use "intake".
 */

import { activeDirectoryRecords } from './advisorFirmDirectory.js';
import type {
  AdvisorFirmDirectory, AdvisorFirmIntakeLabel, AdvisorFirmRecord, AdvisorFirmRosterEntry,
} from './advisorFirmDirectory.js';

/** Compile-time firm identity. Safe in the SPA bundle. */
export interface AdvisorFirmDef {
  /** URL slug (`/intake/{firmId}`) and registry key. Lowercase, stable. */
  readonly firmId: string;
  /** Display name, as UI copy renders it. */
  readonly name: string;
  /** Brand preset key (`src/brand/previewBrands.ts`) and appcore brand record key. */
  readonly brandKey: string;
  /**
   * Branded hosts across envs (S9). Host → firm resolution matches these
   * case-insensitively; a host not listed for any firm is not a firm host.
   *
   * ⚠️ **Adding a hostname here PUBLISHES an advisor-facing link for it immediately**
   * (steward-app#1066). `src/advisor/intakeLink.ts` picks the entry whose parent domain
   * matches the host serving the SPA and shows it to the firm's advisors to copy and send
   * to prospective clients. This list is registry identity, NOT per-environment deployment
   * truth: a branded host only answers once that environment's `BRANDED_HOSTS` variable and
   * wildcard certificate exist, which is a separate operator step with DNS lead time
   * (`deploy/lib/branded-hosts.ts` is explicit that the two "agree by review, per
   * environment, not by import"). So a hostname listed here ahead of its alias is a link an
   * advisor is invited to trust that answers NXDOMAIN or a CloudFront 403. Add the entry in
   * the same change as the alias, or after it. The identical warning applies to a hostname
   * written onto a DIRECTORY record, where there is no PR to review it in at all.
   */
  readonly hostnames: readonly string[];
  /**
   * Copy for the wizard's introductory step, shown once before section 1. Optional:
   * a firm with no `intro` gets none, and the wizard opens straight on section 1 (the
   * behaviour every firm had before this existed).
   *
   * This is the firm's own wording, not a template `IntakeWizard` fills in — a legal
   * or privacy paragraph naming the firm belongs to that firm, and a second firm added
   * later supplies its own `intro` rather than having one interpolated for it. Render
   * `body` verbatim, in order, one paragraph each: it may carry compliance language
   * from the firm's own counsel, which is a request to reproduce exactly, not house
   * copy to bring into the app's usual style.
   */
  readonly intro?: {
    /** e.g. "Your Financial Snapshot". */
    readonly subheading: string;
    /**
     * Paragraphs, rendered in order, verbatim. Each carries its own `kind`
     * rather than being told apart by array position — "the first entry is
     * the lede, the rest are legal" is a rule that breaks the moment a firm
     * needs two lede paragraphs, or opens with the legal paragraph instead.
     * `kind` also drives typography directly: RIPL's own page (and the
     * styling below) renders the lede noticeably larger than the fine print,
     * so the wizard needs to know which is which, not just their order.
     */
    readonly body: ReadonlyArray<{
      readonly kind: 'lead' | 'legal';
      readonly text: string;
    }>;
    /**
     * Label of the button that leaves the intro screen.
     *
     * As of crossmarc/steward-app#1675, that screen is `IntakeWizard.tsx`'s
     * `IntakeConsentGate` — a mandatory sharing-consent screen every prospect sees, not
     * only firms with their own intro copy — and the one click it offers now performs
     * BOTH "leave the intro" and "record consent." Most firms have no pre-existing copy
     * naming that click, so `IntakeConsentGate` falls back to a fixed default ("Agree and
     * begin") when this field is unset. A firm that DOES set it keeps its own label
     * instead, because the label can be load-bearing outside this screen's own control:
     * RIPL's own legal paragraph (quoted verbatim just below, firm-authored compliance
     * copy this type may reproduce but never edit) names its button by this exact text —
     * `By clicking "Get Started,"` — and that click is now the one action the paragraph
     * describes. This field exists so that stays true: it is what keeps
     * `IntakeConsentGate`'s button reading "Get Started" for RIPL specifically, not dead
     * weight to be deleted.
     */
    readonly startLabel: string;
  };
  /**
   * One sentence saying why this firm asks for tax returns, shown after the help text of the
   * form's Tax returns section (crossmarc/steward-app#2187). Optional: a firm with none shows
   * the help text alone. It is a claim about the firm's service, so it is the firm's wording
   * and only RIPL has one (approved text, Matt, 2026-10-16). Read by the SPA from the COMPILED
   * registry only: the directory record and `GET /intake/firm` do not carry it, so a firm that
   * resolves through the directory shows none until that wire is widened.
   */
  readonly taxReturnsWhy?: string;
  /**
   * What profession this firm practises (steward-app#1262). A LABEL — see
   * {@link ADVISOR_FIRM_KINDS}. It grants nothing, gates nothing, and no resolver in this
   * file branches on it; docs/26 forbids a kind that silently granted areas.
   *
   * ⚠️ **`string`, not `AdvisorFirmKind`, and that is not laziness.** The stored field is a
   * bounded string so that an unrecognised value cannot null the whole published directory
   * (see {@link AdvisorFirmRecordSchema.kind} for the full argument), and a resolved firm
   * must therefore be able to carry one. Use `isAdvisorFirmKind()` at the display site;
   * an unrecognised kind renders unlabelled rather than as a raw slug.
   *
   * On the DEF rather than on {@link AdvisorFirm} because it is public identity, like
   * `name` and `brandKey`: safe in the SPA bundle, safe in a log line, and the family's
   * firm picker is an SPA surface. Contrast `tenantId`/`advisorEmails`, which are
   * per-environment config and live on the hydrated type only.
   */
  readonly kind?: string;
  /** The firm's own words for its {@link AdvisorFirmDef.kind}. Display only. */
  readonly kindLabel?: string;
  /**
   * This firm's own word for its intake form. Absent means the caller renders
   * house-neutral copy — NEVER "Financial Snapshot", which is one firm's brand and is the
   * defect this field exists to retire. `src/intake/intakeNoun.ts` owns that fallback.
   */
  readonly intakeLabel?: AdvisorFirmIntakeLabel;
}

/**
 * Where a resolved firm's values came from, in one word.
 *
 * A SERVER CONSTANT — never caller-supplied, never derived from a request — so it is safe
 * to log wherever `firmId` is logged, and that is the point: during the #743 cutover the
 * env leg and the directory leg both answer, and this is the only way to tell which one did.
 */
export type AdvisorFirmSource = 'env' | 'directory';

/** A firm hydrated with its per-environment configuration (façade/writer only). */
export interface AdvisorFirm extends AdvisorFirmDef {
  /** appcore tenant id of the firm (ULID, printed by seed-advisor-firm.ts). */
  readonly tenantId: string;
  /** The firm's own workspace id — where intake submissions live. */
  readonly firmWorkspaceId: string;
  /** Advisor mailbox(es) invited on client connect; disconnect matches against these. */
  readonly advisorEmails: readonly string[];
  /**
   * The firm's published roster of NAMED advisors (steward-app#1343 step 7, 2026-09-09).
   *
   * ⚠️ **This carries an advisor's EMAIL ADDRESS, and it is on {@link AdvisorFirm} rather
   * than on {@link AdvisorFirmDef} for exactly that reason** — the same place
   * `advisorEmails` and `tenantId` live, and for the same rule: per-environment,
   * façade-and-writer only, never in the SPA bundle. `advisorFirms.test.ts` asserts that the
   * SPA-safe def lookups never carry it.
   *
   * 🚨 **Nothing between here and a family is automatic.** This field is the SECOND of three
   * hand-written allowlists between an advisor's mailbox and a browser; the other two are
   * `PROJECTED_FIELDS` (`services/firm-directory/src/index.ts`) and the façade's `items`
   * projection (`handlers/advisor.ts`, `listFirms`), and each has its own test that reads the
   * SERIALIZED body for the address rather than the parsed key set. Widening any of the three
   * puts that address in a family's browser.
   *
   * **DIRECTORY LEG ONLY, and absent rather than empty when there is none.** The env leg
   * (`ADVISOR_FIRM_*`) has no way to express a roster and never sets this, and a record
   * published without one leaves it absent too — which was every firm in dev, stage and prod
   * when the three published documents were read on 2026-09-10. So `advisors === undefined` is
   * the path that actually executes today, and every consumer has to work without it. Absent
   * and `[]` are deliberately NOT distinguished by any consumer: both mean "no named advisor
   * to connect to".
   *
   * 🚨 **That is a reading and it can flip WITHOUT A DEPLOY.** appcore has had two OPERATOR
   * write paths for `advisors` since its PR #641 (merged 2026-09-10T02:38Z): the
   * `seed-advisor-firm` script's `FIRM_ADVISORS`, and `advisors` as one of the five optional
   * fields on `PUT /admin/advisor-firms/{tenantId}`. Only the ops CONSOLE cannot edit one
   * (crossmarc-ops#138). A consumer that treats "there is never a roster" as a precondition
   * rather than as today's data is wrong the first time an operator seeds one.
   *
   * ⚠️ **`listed` is NOT filtered here.** This is the record's roster as published, unlisted
   * entries included; the filtering belongs to whoever offers or targets an advisor, so that
   * exactly one place decides what "offered to families" means. `handlers/advisor.ts` is that
   * place on this repo's family-facing surfaces.
   */
  readonly advisors?: readonly AdvisorFirmRosterEntry[];
  /** Phase B (B1) notification recipient. Unused in Phase A. Env leg only. */
  readonly notifyEmail?: string;
  /** Which leg answered. See {@link AdvisorFirmSource}. */
  readonly source: AdvisorFirmSource;
  /**
   * appcore's `_v` on the firm record this came from; **0 for an env-sourced firm**, which
   * has no record and therefore no version.
   *
   * Stamped into the intake queue message (v2) so the writer can compare the directory the
   * façade resolved against with the one it resolved against itself. That comparison is what
   * replaces the workspace/tenant equality checks `addressedToUs` loses once both sides read
   * one directory — those become the directory agreeing with itself, this one does not.
   */
  readonly recordVersion: number;
  /**
   * Crossmarc's own demo/internal firm (steward-app#1025). **False for every env-sourced
   * firm.** It changes NOTHING about resolution — the intake route answers, the writer
   * stores — and exists so the surfaces that should hide it (the SPA's firm picker, billing
   * and usage metrics) can, without a second lookup.
   *
   * As of steward-app#1226 exactly ONE of those surfaces reads it: the `items` projection of
   * the façade's `GET /advisor/firms` (`handlers/advisor.ts`, `listFirms`), which is the
   * family-facing "Connect an advisor" directory. `advisorFor` and `firmChoice` on that same
   * route deliberately do not — an operator must still be able to act as the demo firm. The
   * billing/metrics half of #1025's requirement is not implemented anywhere yet.
   */
  readonly internal: boolean;
}

/**
 * How a resolver is told where to look.
 *
 * `env` is the process environment (`process.env` on the façade and the background Lambdas,
 * a literal in tests). `directory` is a PARSED, VALIDATED {@link AdvisorFirmDirectory} —
 * `advisorFirmDirectory.ts` owns that parse, and an unparseable document must arrive here as
 * `undefined`, never as a partial one.
 *
 * The resolvers stay SYNCHRONOUS and PURE, which is the decision that keeps #743 tractable:
 * `advisorFirmNameForMailbox` is called from inside the invite throttle, `firmForTenant` from
 * inside `advisorFirmCandidates`, and `resolveAdvisorFirm` from the public intake route before
 * the byte cap. Making any of them async ripples through all three. The LOADING is async, once
 * per invocation, in the caller.
 */
export interface FirmLookupContext {
  readonly env: Record<string, string | undefined>;
  readonly directory?: AdvisorFirmDirectory;
}

/**
 * What every resolver's lookup parameter accepts.
 *
 * ⚠️ **The bare-environment form is TRANSITIONAL.** It exists only so that #743 can migrate
 * ~a dozen call sites one at a time instead of in one unreviewable commit: a caller still
 * passing `process.env` compiles and behaves exactly as it did before, resolving from the env
 * leg alone. Once every caller passes a {@link FirmLookupContext} (#743 PR 6), this union
 * collapses to the context and the `Record` arm is deleted. Do not add a NEW caller on the
 * bare form.
 */
export type FirmLookup = Record<string, string | undefined> | FirmLookupContext;

/**
 * Normalises either accepted lookup form into a context.
 *
 * The discriminator is `typeof env === 'object'`, not `'env' in lookup`. An environment CAN
 * carry a variable literally named `env` — but its value is a string, and a context's is an
 * object, so the two are never confusable. `'env' in lookup` alone would misread that
 * environment as a context with no firms in it, which fails closed but silently.
 */
function toContext(lookup: FirmLookup): FirmLookupContext {
  const candidate = lookup as Partial<FirmLookupContext>;
  if (typeof candidate.env === 'object' && candidate.env !== null) {
    return { env: candidate.env, directory: candidate.directory };
  }
  return { env: lookup as Record<string, string | undefined> };
}

export const ADVISOR_FIRMS: readonly AdvisorFirmDef[] = [
  {
    firmId: 'ripl',
    name: 'RIPL Wealth',
    brandKey: 'ripl',
    hostnames: ['ripl.steward.crossmarc.com', 'ripl.dev.steward.crossmarc.com'],
    kind: 'financial-advisor',
    // ⚠️ **Not cosmetic, and not optional for THIS firm.** "Financial Snapshot" used to be
    // hard-coded as house copy across the wizard, the advisor portal and the intake PDF
    // (steward-app#1262). The fallback is house-neutral now — "financial snapshot", lower
    // case, per Matt's 2026-09-19 decision (it was plain "intake form" before that) — and
    // RIPL's own capitalised proper noun below survives only because it is written HERE.
    // The two now happen to be the SAME WORDS in different case (`intakeNoun.ts`'s docblock
    // calls this out: coincidence, not equivalence) — that makes it easy to mistake this
    // entry for redundant. It is not: deleting it does not fall through to an equal value,
    // it falls through to a lower-case common noun, which renames RIPL's own document out
    // from under its advisors and the families mid-form. It is written on the COMPILED def
    // rather than only on a directory record because the env leg was how RIPL resolved in
    // prod when this was written (`firmFromEnv` spreads the def). ⚠️ steward-app#743 PR 3
    // retired that leg, so RIPL now resolves from its DIRECTORY RECORD, which carries its
    // own `intakeLabel` — prod's did when read at source on 2026-09-09T01:05Z. The compiled
    // def's copy is the fallback for a record that omits it, not the answer prod uses.
    // and the env leg cannot carry a record's fields.
    intakeLabel: { singular: 'Financial Snapshot', plural: 'Financial Snapshots' },
    taxReturnsWhy: 'Your returns let RIPL look for tax opportunities in how things are set up today.',
    intro: {
      subheading: 'Your Financial Snapshot',
      body: [
        {
          kind: 'lead',
          text: 'This takes about 15 minutes. There are no right or wrong answers. We just want to see your full picture so we can build the right strategy around it.',
        },
        // Verbatim, one paragraph, exactly as RIPL's own counsel wrote it —
        // including the final "By clicking" sentence a prior version of this
        // registry entry dropped. Do not reword, re-split, or "fix" the
        // capitalised "Get Started" inside it to match the button copy below;
        // that capitalisation is theirs, quoted, not a typo of ours.
        {
          kind: 'legal',
          text: 'This Financial Snapshot is designed to help RIPL Wealth better understand your current financial situation and prepare for future planning discussions. Completion of this questionnaire does not establish an advisory relationship or constitute investment, legal, or tax advice. Information submitted will be used solely for financial planning and advisory purposes and handled in accordance with RIPL Wealth\'s Privacy Policy. While we take reasonable measures to protect your information, electronic communications may involve risks beyond our control. By clicking "Get Started," you acknowledge that the information provided will be used to evaluate your financial planning and advisory needs.',
        },
      ],
      startLabel: 'Get started',
    },
  },
];

/**
 * The env-var names carrying a firm's per-environment config, derived from the slug
 * (`ripl` → `ADVISOR_FIRM_RIPL_TENANT_ID`, …). One derivation shared by the CDK that
 * sets them and the façade that reads them, so the two cannot drift.
 * `advisorEmails` is comma-separated in the variable.
 */
export function advisorFirmEnvVarNames(firmId: string): {
  tenantId: string; firmWorkspaceId: string; advisorEmails: string; notifyEmail: string;
} {
  const key = firmId.toUpperCase().replace(/-/g, '_');
  return {
    tenantId: `ADVISOR_FIRM_${key}_TENANT_ID`,
    firmWorkspaceId: `ADVISOR_FIRM_${key}_WORKSPACE_ID`,
    advisorEmails: `ADVISOR_FIRM_${key}_ADVISOR_EMAILS`,
    notifyEmail: `ADVISOR_FIRM_${key}_NOTIFY_EMAIL`,
  };
}

/** The compiled registry's own entry for a slug, already normalised. Never the directory. */
function compiledDef(slug: string): AdvisorFirmDef | null {
  return ADVISOR_FIRMS.find((f) => f.firmId === slug) ?? null;
}

/**
 * Every compiled firm this ENVIRONMENT actually configures, hydrated once.
 *
 * Resolved ONCE per filter rather than per record: `firmFromEnv` splits and normalises a
 * comma-separated mailbox list, and a 500-record document would otherwise redo that for
 * every row.
 */
function envConfiguredFirms(env: Record<string, string | undefined>): readonly AdvisorFirm[] {
  const firms: AdvisorFirm[] = [];
  for (const def of ADVISOR_FIRMS) {
    const firm = firmFromEnv(def, env);
    if (firm) firms.push(firm);
  }
  return firms;
}

/**
 * The directory records this lookup may serve: the active ones, **minus every firm the env
 * leg answers for in THIS environment** — by slug, by branded hostname, or by appcore id.
 *
 * This is where "env first, directory second, and never a merge" is actually enforced, and it
 * is one rule rather than a per-resolver one on purpose. The compiled registry and the
 * `ADVISOR_FIRM_*` variables are two halves of ONE deploy-time leg — a def supplies a firm's
 * identity and the variables supply its ids, and `firmFromEnv` needs both — so the unit that
 * can shadow a record is the CONFIGURED firm, not the bare def.
 *
 * Without this, the shadowing is not merely inconsistent, it is exploitable in a small way:
 * a directory record could claim slug `ripl` with its own `hostnames`, and while
 * `resolveAdvisorFirm('ripl')` correctly answered from the env leg, `advisorFirmDefForHostname`
 * would answer from the record for a host the compiled def does not list — so the public intake
 * page would brand and introduce itself from one source while the write path resolved the
 * other. That is a half-env/half-directory firm assembled out of two whole answers, which is
 * the #666 divergence wearing a different hat.
 *
 * 🚨 **The rule is "configured HERE", not "compiled in at all", and that distinction is
 * steward-app#1212** (stage, 2026-09-03). This filter used to drop every record whose slug the
 * compiled registry carried, configured or not, on the argument that the deploy-time leg owns
 * such a slug whether or not the environment fills it in. The consequence, stated plainly in
 * the comment that argued it, was that `ripl` could never resolve from the directory. Stage
 * then ran exactly that configuration — the `ripl` firm published as a directory record, no
 * `ADVISOR_FIRM_RIPL_*` variables on the Lambda — and `GET /advisor/firms` answered
 * `{"items":[],"advisorFor":null}` to the firm's own staff: the firm was quietly missing, which
 * is the failure docs/32 is built around (steward-app#1009), reached by the shadow rule rather
 * than by a bad document.
 *
 * Keying on the CONFIGURED firm loses nothing the rule was protecting. The divergence it
 * prevents needs BOTH legs to answer for one firm; when `firmFromEnv` returns null there is no
 * env answer to diverge from, so the record is the whole firm from one source, exactly as a
 * record on any other slug is. And the env leg still wins WHOLE the moment it is configured:
 * the same variables that make it answer put its slug, its hostnames and its ids in the sets
 * below in the same breath.
 *
 * ⚠️ **The public intake leg's stricter rule is GONE, and so is the asymmetry.**
 * `services/steward-app-api/src/handlers/intake.ts` used to compute `compiledOwnsSlug` and
 * refuse a compiled slug the env leg did not answer for, whatever the directory said, which
 * left an unconfigured compiled slug resolving for the ADVISOR routes while its public intake
 * page 404d (steward-app#1216). steward-app#743 PR 3 retired the env leg and deleted that
 * check in the same release, because with no env leg the predicate refuses every compiled slug
 * rather than guarding one. Both planes now resolve a compiled slug from the directory.
 *
 * ⚠️ **`ADVISOR_FIRMS` is KEPT.** This paragraph used to say "#743 PR 10 deletes `ADVISOR_FIRMS`
 * and the env branch together"; that is not what was decided, and it contradicted Decision 1 of
 * the design note in the file the next person to do the work would read first. What PR 3
 * retired is the env LEG — the `ADVISOR_FIRM_<SLUG>_*` deploy variables and the CDK that emitted
 * them. The registry itself still carries display names and is still what `advisorFirmDef`
 * resolves. `envConfiguredFirms` is consequently empty in every deployed environment, so this
 * filter reduces to the hostname and id rules in practice, but the branch is retained: it is a
 * shared resolver, and a rollback of one deploy re-arms it without a code change.
 */
function servableRecords(ctx: FirmLookupContext): readonly AdvisorFirmRecord[] {
  const records = activeDirectoryRecords(ctx.directory);
  if (records.length === 0) return records;
  const configured = envConfiguredFirms(ctx.env);
  const claimedSlugs = new Set(configured.map((f) => f.firmId));
  // 🚨 **Ids, and this is the check the security review of steward-app#743 PR 6 added.** A
  // record whose slug and hostnames are all its own may still carry a configured firm's
  // `tenantId` or `firmWorkspaceId`, and neither of the other two filters can see that:
  // `AdvisorFirmDirectorySchema`'s `superRefine` enforces uniqueness of both ids, but only
  // WITHIN one document, and the slug check sees the slug, not the ids.
  //
  // What that would buy: `resolveAdvisorFirm('notripl', …)` returning a firm carrying RIPL's
  // tenant id, so a connect stamps `createEngagement({ firmTenantId: <RIPL's> })` against
  // RIPL's own partition while the invitations go to the record's own mailboxes. The env leg
  // cannot express that, so it is a capability the directory would ADD rather than inherit —
  // exactly the class #743 must not add quietly. Same one-sentence rule as the hostname
  // check: **an appcore id belongs to one firm.**
  const claimedIds = new Set(configured.flatMap((f) => [f.tenantId, f.firmWorkspaceId]));
  return records.filter(
    (r) => !claimedSlugs.has(r.slug)
      && !claimsAnotherFirmsCompiledHost(r)
      && !claimedIds.has(r.tenantId)
      && !claimedIds.has(r.firmWorkspaceId),
  );
}

/**
 * True when a record lists a branded hostname that a COMPILED firm with a DIFFERENT slug
 * carries.
 *
 * The slug rule above is not enough on its own. `advisorFirmDefForHostname` checks the compiled
 * registry first, so a record claiming `ripl.steward.crossmarc.com` cannot hijack host→firm
 * resolution or the brand injected on the public intake page. But the record would still resolve
 * by its OWN slug, carrying that host in `hostnames` — and `src/advisor/intakeLink.ts` builds the
 * link a firm's advisors are told to copy and send to prospective clients straight out of that
 * array. Firm B's advisors would be handed a link to firm A's branded intake page.
 *
 * The cross-record uniqueness check in `advisorFirmDirectory.ts` refuses two DIRECTORY records
 * claiming one host; this refuses a directory record claiming ANOTHER firm's COMPILED one,
 * which that check cannot see. Both are the same rule: a hostname belongs to one firm.
 *
 * ⚠️ **`def.firmId !== record.slug` is load-bearing, and it is the hostname half of
 * steward-app#1212.** A record for slug `ripl` listing `ripl.steward.crossmarc.com` is not a
 * squatter, it is the firm's own host arriving from the other leg — and dropping it would
 * reintroduce the stage outage the moment the publisher starts writing hostnames onto that
 * record (stage's document carries `hostnames: []` today, which is the only reason the slug
 * rule alone was the whole bug). The record still cannot take a host from a DIFFERENT compiled
 * firm, and it cannot take its own slug's host away from a configured env leg either: a
 * configured firm's slug is already in `claimedSlugs` above, so the record is gone before this
 * runs.
 */
function claimsAnotherFirmsCompiledHost(record: AdvisorFirmRecord): boolean {
  return record.hostnames.some((h) =>
    ADVISOR_FIRMS.some((def) =>
      def.firmId !== record.slug && def.hostnames.some((c) => c.toLowerCase() === h)));
}

/**
 * The identity half of a directory record, in `AdvisorFirmDef` shape.
 *
 * A projection, not a merge: every field comes from the ONE record. Nothing on a compiled
 * def leaks into it, which is what makes "a firm resolves entirely from one source" true of
 * the def lookups as well as the hydrated ones.
 */
function defFromRecord(record: AdvisorFirmRecord): AdvisorFirmDef {
  return {
    firmId: record.slug,
    name: record.name,
    brandKey: record.brandKey,
    hostnames: record.hostnames,
    ...(record.intro ? { intro: record.intro } : {}),
    ...(record.kind ? { kind: record.kind } : {}),
    ...(record.kindLabel ? { kindLabel: record.kindLabel } : {}),
    ...(record.intakeLabel ? { intakeLabel: record.intakeLabel } : {}),
  };
}

/** A directory record, hydrated. Same rule as {@link defFromRecord}: one record, no merge. */
function firmFromRecord(record: AdvisorFirmRecord): AdvisorFirm {
  return {
    ...defFromRecord(record),
    tenantId: record.tenantId,
    firmWorkspaceId: record.firmWorkspaceId,
    advisorEmails: record.advisorEmails,
    // Spread-when-present, like the optional def fields above: a record with no roster
    // leaves the key ABSENT rather than setting it to `undefined`, so a resolved firm
    // matches its own declared type and `not.toHaveProperty('advisors')` stays a real
    // assertion for the rosterless case rather than one that passes on a present key.
    ...(record.advisors ? { advisors: record.advisors } : {}),
    internal: record.internal,
    recordVersion: record.recordVersion,
    source: 'directory',
  };
}

/**
 * Static def lookup. Null for an unknown slug.
 *
 * Case-insensitive: registry slugs are canonically lowercase, and `/intake/RIPL`
 * should reach the same firm as `/intake/ripl` everywhere rather than each
 * consumer deciding its own normalisation.
 *
 * **SPA-safe when called with one argument** — no env access, no directory, compiled
 * registry only. That is how `src/intake/IntakePage.tsx` and `src/advisor/intakeLink.ts`
 * call it, and it must stay callable that way: the SPA has no directory and gets firm
 * identity by fetch (#743 PR 5).
 *
 * Pass a {@link FirmLookup} on the server to consult the directory. A CONFIGURED compiled firm
 * still wins — the same precedence, and for the same reason, as the hydrated resolvers: the
 * registry and the env vars are one deploy-time leg, and deploy-time truth wins over published
 * data during the cutover.
 *
 * ⚠️ **The order tracks {@link resolveAdvisorFirm}'s exactly, and it has to** (steward-app#1212).
 * These two answer the same question about the same slug — one for identity, one hydrated — so
 * a slug the directory hydrates must be a slug the directory also names, or the public intake
 * page brands itself from the compiled def while the write path resolves the record. The
 * compiled def stays the LAST resort rather than the first: with no lookup (the SPA) and with a
 * lookup that has no record for the slug, this answers exactly as it did before.
 */
export function advisorFirmDef(firmId: string, lookup?: FirmLookup): AdvisorFirmDef | null {
  const slug = firmId.trim().toLowerCase();
  const compiled = compiledDef(slug);
  if (!lookup) return compiled;
  const ctx = toContext(lookup);
  if (compiled && firmFromEnv(compiled, ctx.env)) return compiled;
  const record = servableRecords(ctx).find((r) => r.slug === slug);
  return record ? defFromRecord(record) : compiled;
}

/**
 * Host → firm resolution, case-insensitive, EXACT host match. SPA-safe with one argument;
 * see {@link advisorFirmDef} for what the optional lookup adds.
 *
 * Exact match, never suffix match: `evil.ripl.steward.crossmarc.com` and
 * `ripl.steward.crossmarc.com.evil.example` are different hosts and must not resolve. The
 * directory's own hostname validation (no `*`, no scheme, no port, no path) is what keeps
 * that true of a published record too.
 */
export function advisorFirmDefForHostname(hostname: string, lookup?: FirmLookup): AdvisorFirmDef | null {
  const host = hostname.trim().toLowerCase();
  if (!host) return null;
  const compiled = ADVISOR_FIRMS.find((f) => f.hostnames.some((h) => h.toLowerCase() === host)) ?? null;
  if (!lookup) return compiled;
  const ctx = toContext(lookup);
  // Same precedence as {@link advisorFirmDef}: a CONFIGURED compiled firm owns its hosts, and
  // an unconfigured one yields to its own record (never to another firm's — see
  // `claimsAnotherFirmsCompiledHost`) so host resolution and slug resolution cannot name two
  // different sources for one firm.
  if (compiled && firmFromEnv(compiled, ctx.env)) return compiled;
  const record = servableRecords(ctx)
    .find((r) => r.hostnames.some((h) => h.toLowerCase() === host));
  return record ? defFromRecord(record) : compiled;
}

/**
 * Parses the comma-separated advisor-email env var: trim, lowercase, drop
 * empties and malformed entries, dedupe. Malformed entries are dropped rather
 * than failing the whole firm so one typo cannot silently unconfigure it, but
 * an all-malformed value still resolves to "not configured" (fail closed).
 * Duplicates are removed so the S5 connect flow cannot double-invite a mailbox.
 *
 * The DIRECTORY leg does none of this: a record's mailboxes are already one value per array
 * entry, and `advisorFirmDirectory.ts` REFUSES a record whose addresses are not already
 * trimmed and lowercase rather than normalising them. See that file on why the stored form
 * and the matched form must be the same string.
 */
function parseEmails(raw: string | undefined): string[] {
  if (!raw) return [];
  const emails = raw
    .split(',')
    .map((e) => e.trim().toLowerCase())
    .filter((e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
  return [...new Set(emails)];
}

/**
 * The env leg for one compiled def: all three of tenant id, workspace id and a non-empty
 * mailbox list, or null. Never consults the directory.
 */
function firmFromEnv(def: AdvisorFirmDef, env: Record<string, string | undefined>): AdvisorFirm | null {
  const names = advisorFirmEnvVarNames(def.firmId);
  const tenantId = env[names.tenantId]?.trim();
  const firmWorkspaceId = env[names.firmWorkspaceId]?.trim();
  const advisorEmails = parseEmails(env[names.advisorEmails]);
  if (!tenantId || !firmWorkspaceId || advisorEmails.length === 0) return null;
  const notifyEmail = env[names.notifyEmail]?.trim().toLowerCase();
  return {
    ...def,
    tenantId,
    firmWorkspaceId,
    advisorEmails,
    ...(notifyEmail ? { notifyEmail } : {}),
    source: 'env',
    // No record, so no record version. The writer's version comparison treats 0 as "the
    // env leg answered", which is exactly the state the cutover starts from.
    recordVersion: 0,
    // The env leg predates the internal-firm flag and cannot express it. A firm configured
    // by env vars is a firm someone deployed, which is never the demo firm's provisioning path.
    internal: false,
  };
}

/**
 * Hydrates one firm. Returns null when the slug is unknown to BOTH sources, or when neither
 * source configures it — a missing tenant id, workspace id, or empty advisor-email list all
 * mean "not configured here" on the env leg, and a `status: 'disabled'` record means the same
 * on the directory leg. Never a partially-configured firm.
 *
 * **Env first, directory second, never merged.** If the same slug is configured in both, the
 * env leg wins and the directory record is ignored WHOLE — not field by field. This function
 * is log-free about that; the façade logs it (#743 PR 6), because the interesting event is
 * "the env leg is still answering after the cutover", which is a deployment fact the resolver
 * has no business deciding how to report.
 *
 * `lookup` is passed explicitly (the façade passes a context built from `process.env` plus the
 * published directory) so this module stays SPA-bundle-safe.
 */
export function resolveAdvisorFirm(firmId: string, lookup: FirmLookup): AdvisorFirm | null {
  const slug = firmId.trim().toLowerCase();
  if (!slug) return null;
  const ctx = toContext(lookup);
  // The deploy-time leg owns every slug it CONFIGURES — see `servableRecords`, and
  // steward-app#1212 for why an unconfigured compiled slug falls through to the directory
  // rather than failing closed with a published record sitting right there.
  const def = compiledDef(slug);
  const fromEnv = def ? firmFromEnv(def, ctx.env) : null;
  if (fromEnv) return fromEnv;
  const record = servableRecords(ctx).find((r) => r.slug === slug);
  return record ? firmFromRecord(record) : null;
}

/**
 * Every firm this lookup fully configures, env leg first.
 *
 * A slug the env leg CONFIGURES is never re-offered from the directory: one slug, one
 * firm, one source (`servableRecords`). Order is stable and meaningful — env-sourced firms
 * precede directory-sourced ones — because `advisorFirmNameForMailbox` and `firmForTenant`
 * both resolve ties by taking the first match, and "the deploy-time answer wins" is the
 * tie-break this whole cutover rests on.
 */
export function listAdvisorFirms(lookup: FirmLookup): AdvisorFirm[] {
  const ctx = toContext(lookup);
  const firms: AdvisorFirm[] = [...envConfiguredFirms(ctx.env)];
  // No dedupe needed and none written: `servableRecords` has already dropped every configured
  // slug, and `activeDirectoryRecords` has already dropped duplicate slugs within the document.
  // A dedupe here would hide a change to either of those rather than defend against one.
  for (const record of servableRecords(ctx)) firms.push(firmFromRecord(record));
  return firms;
}

/**
 * The DISPLAY NAME of the firm `email` is an advisor mailbox of, or null
 * (steward-app#630). Doubles as the "is this a registry mailbox?" predicate: a non-null
 * answer is what makes the invite throttle treat the address as a first-party partner
 * mailbox rather than a caller-chosen third party.
 *
 * ⚠️ **A property of the address, decided by the resolvers' sources, and assertable by
 * nobody.** The throttle uses this to pick which per-recipient daily LIMIT applies — a
 * decision about the fleet's anti-harassment control — so how it is DERIVED is the whole
 * safety argument. It reads `ADVISOR_FIRMS` (compiled in), `ADVISOR_FIRM_*` (set by the CDK
 * from the prod seeding ceremony) and, after #743, the published directory, and nothing else.
 * There is deliberately no boolean a call site can pass to declare a recipient first-party:
 * such a flag would be one careless future call site away from loosening the cap for an
 * arbitrary address, and a control a caller can opt out of is not a control.
 *
 * ⚠️ **Once the directory answers, whoever can write a firm record can widen a throttle**
 * from the strict per-recipient cap to the generous first-party one (docs/32 §7 checklist 6).
 * Since steward-app#1212 the slugs that applies to include COMPILED ones this environment
 * does not configure: a record on slug `ripl` in an environment with no `ADVISOR_FIRM_RIPL_*`
 * now resolves, so its `advisorEmails` meet the generous cap where they previously met the
 * strict one. That is the same accepted capability one slug wider, not a new class — the
 * write path is still operator-only and the throttle still logs the firm and `source` at the
 * moment the generous limit applies.
 * That is a deliberate, reviewed consequence of retiring the compiled registry, and it is why
 * the write path is operator-only (appcore `/admin/advisor-firms`, no member-plane route) and
 * why the throttle logs the firm name and `source` at the moment the generous limit applies
 * (#743 PR 6). Nothing here is un-capped; the generous ceiling is still a ceiling.
 *
 * ⚠️ **It returns a NAME, not the firm.** `AdvisorFirm` carries `tenantId`,
 * `firmWorkspaceId`, `advisorEmails` and `notifyEmail` — per-environment config that is
 * façade-only and must never reach a response body or a log line. The one caller logs
 * twice into a 90-day log group, so the narrow return type is what makes "no per-env
 * config leaked here" structural rather than remembered. The name is public identity, and the
 * directory bounds its charset (no control characters) precisely because it is logged there.
 *
 * The match is EXACT on the same normalisation `parseEmails` applies (trim, lowercase),
 * NOT the rate limiter's canonical `throttleKey` form. Narrower on purpose:
 * `advisor+anything@firm.test` is a distinct address a caller could type, and it keeps
 * the strict limit. Widening this to the canonical form would hand anyone who can guess a
 * firm's domain the generous one.
 *
 * Null for every address in an environment that configures no firm at all — which is
 * every unit test and every fresh environment, so the default answer is "the strict cap
 * applies".
 *
 * The first configured firm listing the address wins; a mailbox shared between two firms
 * is a registry mistake, not a case to resolve at request time.
 */
export function advisorFirmNameForMailbox(email: string, lookup: FirmLookup): string | null {
  return advisorFirmForMailbox(email, lookup)?.name ?? null;
}

/**
 * What a first-party mailbox match is, for the caller that has to SAY it happened.
 *
 * Three fields, and the omissions are the type: no `tenantId`, no `firmWorkspaceId`, no
 * `advisorEmails`, no `notifyEmail`. Everything {@link advisorFirmNameForMailbox}'s ⚠️
 * refuses to hand its caller is still refused here — this adds the firm's SLUG and which
 * leg answered, both of which are public identity and server constants, and nothing else.
 */
export interface AdvisorFirmMailboxMatch {
  readonly firmId: string;
  readonly name: string;
  readonly source: AdvisorFirmSource;
}

/**
 * The firm identity behind {@link advisorFirmNameForMailbox}'s answer, or null.
 *
 * ⚠️ **ONE decision, not two** (steward-app#666's lesson, applied to this pair). The
 * throttle decides which per-recipient cap applies from `advisorFirmNameForMailbox`, and
 * docs/32 §7 checklist 6 requires it to LOG the firm and the source at the moment the
 * generous cap is chosen. A second matcher written beside the first — even one as small as
 * "find the firm whose name equals the name we just got" — is a second place the match rule
 * can drift from, and the whole point of checklist 6 is that this control is now data-driven
 * and must therefore be observable EXACTLY where it is applied. So the name getter is the
 * projection and this is the function; there is no path by which the log line can describe a
 * different match from the one the cap was chosen by.
 *
 * Everything {@link advisorFirmNameForMailbox} documents about the match applies unchanged:
 * exact on trim+lowercase, never the rate limiter's canonical form, first configured firm
 * wins, null in an environment that configures no firm.
 */
export function advisorFirmForMailbox(email: string, lookup: FirmLookup): AdvisorFirmMailboxMatch | null {
  const candidate = email.trim().toLowerCase();
  if (!candidate) return null;
  const firm = listAdvisorFirms(lookup).find((f) => f.advisorEmails.includes(candidate));
  return firm ? { firmId: firm.firmId, name: firm.name, source: firm.source } : null;
}

/**
 * The firm a tenant id belongs to, or null. Advisor detection (docs/25 §1) keys on
 * this: a user whose membership tenant matches a registered firm is an advisor.
 * UX gating only — never authorization.
 *
 * The comparison is EXACT — no trim, no case folding. The stored ids are already exact
 * (strict Crockford on the directory leg, `.trim()`ed on the env leg), so normalising the
 * incoming value here would only ever widen the match.
 */
export function firmForTenant(tenantId: string, lookup: FirmLookup): AdvisorFirm | null {
  return listAdvisorFirms(lookup).find((f) => f.tenantId === tenantId) ?? null;
}
